import { Component, Vue } from 'vue-property-decorator';
import { ProfessionModule } from '@/store/modules/profession';
import { AppModule } from '@/store/modules/app';
import { JobType } from '@/types/ProfessionType';
import { extractJobFilters } from '@/api/AiTool';
import { groupExtendedJobFilters } from '@/config/configuration';
import { AI_ONLY_TYPES, SWITCH_GROUPS } from '@/sections/professionChips';

@Component({
  name: 'AiJobSearchMixin',
})
export default class AiJobSearchMixin extends Vue {
  // The hint "please name a profession" goes with the AI search itself — every site that has
  // it enabled in the portal settings (app setting enable_ai_jobs_search).
  get isAiProfessionHintEnabled() {
    return Number(AppModule.setting?.enable_ai_jobs_search) === 1;
  }

  // Job types from the AI (v=2). The list filters can only EXCLUDE a type (filter = 1), as the
  // switches of the advanced search do. So "Vollzeit" excludes the other types of the site's
  // employment switches, and "kein Praktikum" excludes Praktikum. The types without a switch
  // (homeoffice, freelance, …) can only be excluded ("kein Homeoffice"); "only home office"
  // has no list filter, so true is ignored for them.
  // Returns whether a filter was set.
  applyAiJobTypes(jobTypes: Record<string, boolean | null>): boolean {
    const ruledOut = AI_ONLY_TYPES.filter((type) => jobTypes[type] === false);
    ruledOut.forEach((type) => ProfessionModule.SetFilter({ obj: type, value: 1 }));
    const switchesSet = this.applyAiJobSwitches(jobTypes);
    return switchesSet || ruledOut.length > 0;
  }

  // The types of the site's employment switches (the advanced search's own filters)
  applyAiJobSwitches(jobTypes: Record<string, boolean | null>): boolean {
    const group = groupExtendedJobFilters(AppModule.sid) ? 'kle' : 'employment';
    const switches = SWITCH_GROUPS[group].map(([filter]) => filter);

    const wanted = switches.filter((type) => jobTypes[type] === true);
    const excluded = switches.filter((type) => {
      // a wanted type: every other type of the group goes
      if (wanted.length) return !wanted.includes(type);
      // no wanted type: only the ones the text rules out
      return jobTypes[type] === false;
    });
    // all excluded = no filter at all (as removeSwitchChip does), so nothing to set
    if (!excluded.length || excluded.length === switches.length) return false;

    excluded.forEach((type) => ProfessionModule.SetFilter({ obj: type, value: 1 }));
    return true;
  }

  async applyAiFilters(text: string, isSearch = false) {
    const normalizedText = text?.trim();
    if (!normalizedText) {
      if (isSearch) {
        ProfessionModule.SetLastAiSearchText('');
        ProfessionModule.SetLastAiSearchEmpty(true);
        ProfessionModule.SET_QUERY_SEARCH_DEFAULT();
        ProfessionModule.SET_FILTER_DEFAULT();
        ProfessionModule.SET_SEARCH_DEFAULT();
        ProfessionModule.SET_PARAMS_DEFAULT();
        ProfessionModule.SET_TAGS_DEFAULT();
        ProfessionModule.SetProfessionTitle('');
        ProfessionModule.SetJobType(JobType.search);

        AppModule.SetVisibleMap(false);
        AppModule.SetVisibleJobs(true);
        AppModule.SetVisibleClusterJobs(false);
        AppModule.SET_WELCOME(true);
        this.$router.push({ name: 'Jobs', query: { lang: AppModule.language } }).catch(() => {});
      }
      return;
    }
    try {
      ProfessionModule.SetLastAiSearchText(normalizedText);
      const response = await extractJobFilters({ text: normalizedText, sid: AppModule.languageSid });
      const f = response?.filters;
      if (!f) {
        ProfessionModule.SetLastAiSearchEmpty(true);
        ProfessionModule.SetAiSearchNeedsProfession(this.isAiProfessionHintEnabled);
        return;
      }

      if (isSearch) {
        ProfessionModule.SET_QUERY_SEARCH_DEFAULT();
        ProfessionModule.SET_FILTER_DEFAULT();
        ProfessionModule.SET_SEARCH_DEFAULT();
        ProfessionModule.SET_PARAMS_DEFAULT();
        ProfessionModule.SET_TAGS_DEFAULT();
        ProfessionModule.SetProfessionTitle('');
        ProfessionModule.SetJobType(JobType.search);
      }

      if (f.professions?.length) {
        const professionItems = f.professions.map((p: { code: string, name: string }) => ({
          id: p.code,
          name: p.name,
          title: p.name,
        }));
        ProfessionModule.SetSearchJobProfession(professionItems);
        ProfessionModule.SetSearchJobProfessionTitle(professionItems);
        ProfessionModule.SetSearch({
          obj: 'profession_id',
          value: professionItems.map((v: any) => v.id).join(','),
        });
      } else {
        ProfessionModule.SetSearchJobProfession(null);
        ProfessionModule.SetSearchJobProfessionTitle(null);
        ProfessionModule.SetSearch({ obj: 'profession_id', value: null });
      }

      if (f.locations?.length) {
        const loc = f.locations[0];
        const locationObj = { id: loc.id, name: loc.name };
        ProfessionModule.SetSearchJobLocation(locationObj);
        ProfessionModule.SetSearchJobLocationTitle(locationObj);
        ProfessionModule.SetSearch({ obj: 'location_id', value: loc.id });
      } else {
        ProfessionModule.SetSearchJobLocation(null);
        ProfessionModule.SetSearchJobLocationTitle(null);
        ProfessionModule.SetSearch({ obj: 'location_id', value: null });
      }

      if (f.radius != null && f.radius > 0) {
        const radiusVal = Number(f.radius);
        ProfessionModule.SetSearch({ obj: 'radius', value: radiusVal });
        ProfessionModule.SetSearchTags({ tag: 'radius', value: radiusVal });
      } else {
        ProfessionModule.SetSearch({ obj: 'radius', value: 0 });
        ProfessionModule.SetSearchTags({ tag: 'radius', value: null });
      }

      const qParts = [...new Set([
        ...(f.skills || []),
        ...(f.context_keywords || []),
      ].filter(Boolean))];
      if (qParts.length) {
        const q = qParts.join(' ');
        ProfessionModule.SetSearch({ obj: 'q', value: q });
        ProfessionModule.SetSearch({ obj: 'qmode', value: 2 });
        ProfessionModule.SetSearchTags({ tag: 'textTag', value: q });
        ProfessionModule.SetSearchTags({ tag: 'keywordTags', value: qParts });
      } else {
        ProfessionModule.SetSearch({ obj: 'q', value: '' });
        ProfessionModule.SetSearchTags({ tag: 'textTag', value: null });
        ProfessionModule.SetSearchTags({ tag: 'keywordTags', value: null });
      }

      const typeFiltersSet = this.applyAiJobTypes(f.job_types || {});

      const hasAnyFilters = !!(
        f.professions?.length
        || f.locations?.length
        || (f.radius != null && f.radius > 0)
        || qParts.length
        || typeFiltersSet
      );
      ProfessionModule.SetLastAiSearchEmpty(!hasAnyFilters);
      // A query without a profession (e.g. only "kindergarten") would list every job,
      // so the list shows a hint to name the profession instead.
      ProfessionModule.SetAiSearchNeedsProfession(this.isAiProfessionHintEnabled && !f.professions?.length);

      if (isSearch) {
        AppModule.SetVisibleMap(false);
        AppModule.SetVisibleJobs(true);
        AppModule.SetVisibleClusterJobs(false);
        AppModule.SET_WELCOME(true);
        this.$router.push({ name: 'Jobs', query: { lang: AppModule.language } }).catch(() => {});
      }
    } catch (err) {
      console.error('AiJobSearchMixin.applyAiFilters', err);
    }
  }
}
